import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Paper, Typography, CircularProgress, Chip, Tooltip, IconButton } from '@mui/material';
import { getSequence } from '../utils/sequences';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

interface SequenceViewerProps {
  proteinId: string; // UniProt accession or gene symbol
  highlightCysteine?: string; // e.g., 'C123'
  observedCysteines?: string[]; // e.g., ['C45', 'C123']
  hyperreactiveCysteines?: string[]; // e.g., ['C123']
  onCysteineClick?: (cys: string) => void;
}

const WRAP_COLS = 60; // characters per line

function parseCysteinePos(cys?: string): number | null {
  if (!cys) return null;
  const m = cys.match(/^C(\d+)$/i);
  if (!m) return null;
  return parseInt(m[1], 10);
}

export const SequenceViewer: React.FC<SequenceViewerProps> = ({
  proteinId,
  highlightCysteine,
  observedCysteines = [],
  hyperreactiveCysteines = [],
  onCysteineClick,
}) => {
  const [sequence, setSequence] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setError(null);
    setSequence(null);

    (async () => {
      const seq = await getSequence(proteinId);
      if (!mounted) return;
      if (!seq) {
        setError('Sequence not available from static data or UniProt');
        setLoading(false);
        return;
      }
      setSequence(seq);
      setLoading(false);
    })();

    return () => {
      mounted = false;
    };
  }, [proteinId]);

  const highlightPos = useMemo(() => {
    if (!sequence || sequence.length === 0) return null as number | null;
    const pos = parseCysteinePos(highlightCysteine);
    const fallbackPos = sequence.indexOf('C') >= 0 ? sequence.indexOf('C') + 1 : 1;
    return pos && pos > 0 && pos <= sequence.length ? pos : fallbackPos;
  }, [sequence, highlightCysteine]);

  const observedSet = useMemo(() => new Set(observedCysteines.map(c => c.toUpperCase())), [observedCysteines]);
  const hyperSet = useMemo(() => new Set(hyperreactiveCysteines.map(c => c.toUpperCase())), [hyperreactiveCysteines]);

  const containerRef = useRef<HTMLDivElement>(null);
  const highlightedRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    // Scroll highlight into view after render
    if (highlightedRef.current && containerRef.current) {
      const el = highlightedRef.current;
      const container = containerRef.current;
      const elRect = el.getBoundingClientRect();
      const cRect = container.getBoundingClientRect();
      if (elRect.top < cRect.top || elRect.bottom > cRect.bottom) {
        el.scrollIntoView({ block: 'center', behavior: 'smooth' });
      }
    }
  }, [sequence, highlightPos]);

  // Build wrapped content before any early returns to satisfy rules-of-hooks
  const wrappedContent = useMemo(() => {
    if (!sequence) return null;
    const lines: Array<{ start: number; end: number; text: string }> = [];
    for (let i = 0; i < sequence.length; i += WRAP_COLS) {
      const s = i + 1;
      const e = Math.min(sequence.length, i + WRAP_COLS);
      lines.push({ start: s, end: e, text: sequence.slice(i, e) });
    }
    return (
      <div>
        {lines.map((line, li) => (
          <div key={li} style={{ display: 'flex', alignItems: 'baseline', gap: 12 }}>
            <div style={{ width: 70, textAlign: 'right', color: '#777' }}>{line.start.toString().padStart(5, ' ')}</div>
            <div>
              {line.text.split('').map((char, ci) => {
                const absolutePos = line.start + ci;
                const isC = char === 'C';
                const cId = `C${absolutePos}`;
                const isHyper = isC && hyperSet.has(cId);
                const isObserved = isC && observedSet.has(cId);
                const isHighlight = highlightPos === absolutePos;

                const baseStyle: React.CSSProperties = {
                  padding: '0 2px',
                  borderRadius: 2,
                  cursor: isC ? 'pointer' : 'default',
                  display: 'inline-block',
                  marginRight: ((ci + 1) % 10 === 0) ? 6 : 0,
                };

                let style = baseStyle;
                if (isC) {
                  style = {
                    ...baseStyle,
                    color: isHighlight ? '#fff' : isHyper ? '#b71c1c' : isObserved ? '#d84315' : '#388e3c',
                    backgroundColor: isHighlight ? '#1976d2' : isHyper ? '#ffcdd2' : isObserved ? '#ffe0b2' : '#e8f5e9',
                    fontWeight: isHighlight ? 700 : 600,
                    border: isHyper ? '1px solid #ef9a9a' : isObserved ? '1px solid #ffcc80' : '1px solid #c8e6c9',
                  };
                }

                const spanProps: any = {
                  style,
                  title: `Position ${absolutePos}${isC ? (isHyper ? ' • Hyperreactive' : isObserved ? ' • Observed' : ' • Unobserved') : ''}`,
                  onClick: () => {
                    if (isC && onCysteineClick) onCysteineClick(cId);
                  },
                  ref: isHighlight ? highlightedRef : undefined,
                };

                return (
                  <span key={ci} {...spanProps}>
                    {char}
                  </span>
                );
              })}
            </div>
            <div style={{ width: 70, color: '#777' }}>{line.end.toString().padStart(5, ' ')}</div>
          </div>
        ))}
      </div>
    );
  }, [sequence, observedSet, hyperSet, highlightPos, onCysteineClick]);

  if (loading) {
    return (
      <Paper sx={{ p: 3, height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Paper>
    );
  }

  if (error || !sequence) {
    return (
      <Paper sx={{ p: 3 }}>
        <Typography color="error">{error || 'Sequence unavailable'}</Typography>
      </Paper>
    );
  }

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(sequence);
    } catch (_) {}
  };

  return (
    <Paper sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <Typography variant="h6" gutterBottom>Sequence</Typography>
          <Typography variant="body2" color="text.secondary">
            {proteinId} • Length {sequence.length}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Chip label={`Observed C: ${observedSet.size}`} size="small" />
          <Chip label={`Hyper C: ${hyperSet.size}`} color="error" size="small" />
          <Tooltip title="Copy full sequence">
            <IconButton onClick={copyToClipboard} size="small"><ContentCopyIcon fontSize="small" /></IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Box ref={containerRef} sx={{ fontFamily: 'monospace', fontSize: '13px', lineHeight: 1.8, p: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1, flex: 1, overflow: 'auto', backgroundColor: '#fafafa' }}>
        {wrappedContent}

        <Box sx={{ display: 'flex', gap: 2, mt: 2, color: 'text.secondary', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <span style={{ display: 'inline-flex', width: 16, height: 16, background: '#1976d2', borderRadius: 2 }} />
            <Typography variant="caption">Current C</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <span style={{ display: 'inline-flex', width: 16, height: 16, background: '#ffcdd2', border: '1px solid #ef9a9a', borderRadius: 2 }} />
            <Typography variant="caption">Hyperreactive</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <span style={{ display: 'inline-flex', width: 16, height: 16, background: '#ffe0b2', border: '1px solid #ffcc80', borderRadius: 2 }} />
            <Typography variant="caption">Observed</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <span style={{ display: 'inline-flex', width: 16, height: 16, background: '#e8f5e9', border: '1px solid #c8e6c9', borderRadius: 2 }} />
            <Typography variant="caption">Unobserved C</Typography>
          </Box>
        </Box>
      </Box>
    </Paper>
  );
};

export default SequenceViewer;
